import { effect, inject, Injectable, ResourceRef, signal, StateKey, WritableSignal } from '@angular/core';
// eslint-disable-next-line no-restricted-imports
import { rxResource } from '@angular/core/rxjs-interop';
import { InfrastructureService } from 'angular-api-gateway/v1/api/infrastructure.service';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { GetFrontendFeaturesResponse } from 'angular-api-gateway/v1/model/getFrontendFeaturesResponse';
import { GetFrontendFeaturesFeature } from 'angular-api-gateway/v1/model/getFrontendFeaturesFeature';

import { EXP_CACHE_KEY } from './constants';
import { GrowthBookValue } from './growth-book.enums';
import { isSameGrowthBookValue } from './growth-book-value.util';
import { GrowthBookCacheService } from './growth-book.cache.service';
import { GrowthBookOverridesStore } from './growth-book-overrides.store';
import { GBFeatureValue } from './interfaces';
import { PlatformService } from '@lentau/ui-kit/core/services/platform/platform.service';
import { GrowthBookService } from './growth-book.service';
import { FeatureDefinition } from '@growthbook/growthbook';

const REQUEST_CACHE_KEY: StateKey<string> = EXP_CACHE_KEY as StateKey<string>;

@Injectable({
  providedIn: 'root',
})
export class GrowthBookRequestBuilder {
  private growthBookCacheService: GrowthBookCacheService = inject(GrowthBookCacheService);
  private growthBookService: GrowthBookService = inject(GrowthBookService);
  private overridesStore: GrowthBookOverridesStore = inject(GrowthBookOverridesStore);
  private infrastructureService: InfrastructureService = inject(InfrastructureService);
  private platformService: PlatformService = inject(PlatformService);

  private paramsSignal: WritableSignal<boolean> = signal(false);
  private allowedExperimentKeysResource: ResourceRef<Set<string> | undefined> = rxResource<Set<string>, boolean>({
    params: this.paramsSignal,
    stream: (): Observable<Set<string>> => {
      if (this.platformService.isServer) {
        return of(new Set<string>());
      }

      return this.infrastructureService.getFrontendFeatures().pipe(
        map((response: GetFrontendFeaturesResponse) => {
          const features: GetFrontendFeaturesFeature[] = response?.features ?? [];
          const activeFeatures: GetFrontendFeaturesFeature[] = features.filter(
            (feature: GetFrontendFeaturesFeature) => !feature.tags?.includes('deprecated')
          );

          return new Set(activeFeatures.map((feature: GetFrontendFeaturesFeature) => feature.id));
        }),
        catchError((error: unknown) => {
          console.error('GrowthBookRequestBuilder: failed to load frontend features', error);
          return of(new Set<string>());
        })
      );
    },
  });

  private requestExperiments: string = '';

  constructor() {
    this.setAllowedExperiments();
    this.requestAllowedExperiments();
  }

  public getRequestExperiments(): string {
    if (this.requestExperiments) {
      return this.requestExperiments;
    }

    const cache: string = this.growthBookCacheService.getCacheItem(REQUEST_CACHE_KEY);
    if (cache) {
      this.requestExperiments = this.excludeDefaultExperiments(cache);
      return this.requestExperiments;
    }

    return '';
  }

  public requestAllowedExperiments(): void {
    const value: Set<string> | undefined = this.allowedExperimentKeysResource.value();
    const isLoading: boolean = this.allowedExperimentKeysResource.isLoading();
    if (value === undefined && !isLoading) {
      // Обновляем сигнал параметров, чтобы триггернуть загрузку ресурса
      this.paramsSignal.set(true);
    }
  }

  private setAllowedExperiments(): void {
    effect(() => {
      const isLoading: boolean = this.allowedExperimentKeysResource.isLoading();
      const allowedKeys: Set<string> | undefined = this.allowedExperimentKeysResource.value();
      const hasValue: boolean = allowedKeys !== undefined;
      const isInitialized: boolean = this.growthBookService.isInitialized();
      const cachedFeatures: Record<string, FeatureDefinition<GBFeatureValue> | GBFeatureValue> =
        this.growthBookService.getCachedFeatures();

      this.growthBookService.getAttributesVersion();

      if (isLoading || !hasValue || !allowedKeys?.size || !isInitialized) {
        return;
      }

      const featureKeys: string[] = Object.keys(cachedFeatures);
      if (!featureKeys.length) {
        return;
      }

      const { requestHeaderExp, ssrExperiments }: { requestHeaderExp: string[]; ssrExperiments: string[] } =
        this.buildExperimentsLists(allowedKeys);

      if (!requestHeaderExp.length && !ssrExperiments.length) {
        return;
      }

      const headerValue: string = requestHeaderExp.join(', ');
      const ssrCookieValue: string = ssrExperiments.join(', ');

      this.growthBookCacheService.setRequestExpToCache(REQUEST_CACHE_KEY, headerValue, ssrCookieValue);
      this.requestExperiments = headerValue;
    });
  }

  private buildExperimentsLists(allowedKeys: Set<string>): { requestHeaderExp: string[]; ssrExperiments: string[] } {
    const cachedFeatures: Record<string, FeatureDefinition<GBFeatureValue> | GBFeatureValue> =
      this.growthBookService.getCachedFeatures();
    const featureKeys: string[] = Object.keys(cachedFeatures);

    if (!featureKeys.length) {
      return { requestHeaderExp: [], ssrExperiments: [] };
    }

    const requestHeaderExp: string[] = [];
    const ssrExperiments: string[] = [];

    for (const key of featureKeys) {
      if (!key.startsWith('exp_')) {
        continue;
      }

      const keyValue: string = this.getFeatureKeyValue(key);
      if (keyValue) {
        if (allowedKeys.has(key)) {
          requestHeaderExp.push(keyValue);
        }

        if (this.growthBookCacheService.isSsrExperiment(keyValue)) {
          ssrExperiments.push(keyValue);
        }
      }
    }

    return { requestHeaderExp, ssrExperiments };
  }

  private getFeatureKeyValue(key: string): string {
    const rawValue: GBFeatureValue =
      this.overridesStore.getValue(key) ?? this.growthBookService.getFeatureValue(key).value;

    if (!rawValue || typeof rawValue === 'object') {
      return '';
    }

    const strValue: string = String(rawValue);

    if (isSameGrowthBookValue(strValue, GrowthBookValue.Default)) {
      return '';
    }

    const isValid: boolean = /^[a-zA-Z0-9_]+$/.test(strValue);
    return isValid ? `${key}.${strValue}` : '';
  }

  private excludeDefaultExperiments(experiments: string): string {
    if (!experiments) {
      return '';
    }

    return experiments
      .split(',')
      .map((experiment: string) => experiment.trim())
      .filter((experiment: string) => experiment.length > 0 && !this.hasDefaultVariant(experiment))
      .join(', ');
  }

  private hasDefaultVariant(experiment: string): boolean {
    const separatorIndex: number = experiment.lastIndexOf('.');
    if (separatorIndex < 0) {
      return false;
    }

    return isSameGrowthBookValue(experiment.slice(separatorIndex + 1), GrowthBookValue.Default);
  }
}
